import { Component, signal, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';

// Services
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    CardModule,
    InputTextModule,
    PasswordModule,
    ButtonModule,
    MessageModule,
    CheckboxModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  // Services
  private readonly authService = inject(AuthService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  // Loading state
  protected readonly isLoading = signal(false);

  // Define individual form controls
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  passwordControl = new FormControl('', [Validators.required, Validators.minLength(8)]);
  stayLoggedInControl = new FormControl(false);

  // Create form group using the individual controls
  loginForm = new FormGroup({
    email: this.emailControl,
    password: this.passwordControl,
    stayLoggedIn: this.stayLoggedInControl,
  });

  onSubmit(): void {
    if (!this.loginForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading.set(true);

    const credentials = {
      email: this.emailControl.value!,
      password: this.passwordControl.value!,
      stayLoggedIn: this.stayLoggedInControl.value ?? false,
    };

    this.authService.login(credentials).subscribe({
      next: (user) => {
        console.log('✅ Login successful:', user);
        this.router.navigate(['/platform-insights']);
      },
      error: (error) => {
        this.isLoading.set(false);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  getEmailErrorMessage(): string {
    if (this.emailControl.errors?.['required']) {
      return this.translateService.instant('login.errors.emailRequired');
    }
    if (this.emailControl.errors?.['email']) {
      return this.translateService.instant('login.errors.emailInvalid');
    }
    return '';
  }

  getPasswordErrorMessage(): string {
    if (this.passwordControl.errors?.['required']) {
      return this.translateService.instant('login.errors.passwordRequired');
    }
    if (this.passwordControl.errors?.['minlength']) {
      return this.translateService.instant('login.errors.passwordMinLength');
    }
    return '';
  }

  private markFormGroupTouched(): void {
    this.emailControl.markAsTouched();
    this.passwordControl.markAsTouched();
  }
}
